if (!customElements.get('product-info')) {
    customElements.define(
      'product-info',
      class ProductInfo extends HTMLElement {
        onVariantChangeUnsubscriber = undefined;
        cartUpdateUnsubscriber = undefined;
        abortController = undefined;
        pendingRequestUrl = null;
        preProcessHtmlCallbacks = [];
        postProcessHtmlCallbacks = [];

        constructor() {
          super();
          this.scrollsnap = this.querySelector('scroll-snap');
        }

        connectedCallback() {
          this.initializeProductSwapUtility();

          this.onVariantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleOptionValueChange.bind(this)
          );

          this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

          this.updateNostoPrices();
        }

        addPreProcessCallback(callback) {
          this.preProcessHtmlCallbacks.push(callback);
        }

        disconnectedCallback() {
          this.onVariantChangeUnsubscriber();
          this.cartUpdateUnsubscriber?.();
        }

        initializeProductSwapUtility() {
          this.postProcessHtmlCallbacks.push((newNode) => {
            window?.Shopify?.PaymentButton?.init();
            window?.ProductModel?.loadShopifyXR();
          });
        }

        updateNostoPrices() {
          function waitForElement(selector, callback) {
            const observer = new MutationObserver((mutations, observer) => {
              const element = document.querySelector(selector);
              if (element) {
                  observer.disconnect();
                  callback(element);
              }
            });

            observer.observe(document.body, {
              childList: true,
              subtree: true,
            });
          }

          function removeTrailingZeros(priceString) {
            // First convert to a number to handle the decimal places
            const number = parseFloat(priceString.replace(/[^0-9.-]+/g, ''));

            // Format the number without trailing zeros
            const formatted = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 2
            }).format(number);

            return formatted;
          }

          // Element is dynamically injected, so we need to wait for it
          waitForElement('.featured-collection-section', (element) => {
            const nostoPrices = element.querySelectorAll('.nosto-price');
            nostoPrices.forEach(price => {
              const priceWithoutZeros = removeTrailingZeros(price.textContent);
              price.textContent = priceWithoutZeros;
            });
          });
        }

        handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
          if (!this.contains(event.target)) return;

          this.resetProductFormState();

          const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
          this.pendingRequestUrl = productUrl;
          const shouldSwapProduct = this.dataset.url !== productUrl;
          const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
          this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
            targetId: target.id,
            callback: shouldSwapProduct
              ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
              : this.handleUpdateProductInfo(productUrl),
          });
        }

        resetProductFormState() {
          const productForm = this.productForm;
          productForm?.toggleSubmitButton(true);
          productForm?.handleErrorMessage();
        }

        handleSwapProduct(productUrl, updateFullPage) {
          return (html) => {
            this.productModal?.remove();

            const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
            const variant = this.getSelectedVariant(html.querySelector(selector));
            this.updateURL(productUrl, variant?.id);

            if (updateFullPage) {
              document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

              HTMLUpdateUtility.viewTransition(
                document.querySelector('main'),
                html.querySelector('main'),
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            } else {
              HTMLUpdateUtility.viewTransition(
                this,
                html.querySelector('product-info'),
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            }
          };
        }

        renderProductInfo({ requestUrl, targetId, callback }) {
          this.abortController?.abort();
          this.abortController = new AbortController();

          fetch(requestUrl, { signal: this.abortController.signal })
            .then((response) => response.text())
            .then((responseText) => {
              this.pendingRequestUrl = null;
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              callback(html);
            })
            .then(() => {
              // set focus to last clicked option value
              document.querySelector(`#${targetId}`)?.focus();
            })
            .catch((error) => {
              if (error.name === 'AbortError') {
                console.log('Fetch aborted by user');
              } else {
                console.error(error);
              }
            });
        }

        getSelectedVariant(productInfoNode) {
          const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
          return !!selectedVariant ? JSON.parse(selectedVariant) : null;
        }

        buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
          const params = [];

          !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

          if (optionValues.length) {
            params.push(`option_values=${optionValues.join(',')}`);
          }

          return `${url}?${params.join('&')}`;
        }

        updateOptionValues(html) {
          const variantSelects = html.querySelector('variant-selects');
          if (variantSelects) {
            HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          }
        }

        handleUpdateProductInfo(productUrl) {
          return (html) => {
            const variant = this.getSelectedVariant(html);

            this.pickupAvailability?.update(variant);
            this.updateOptionValues(html);
            this.updateURL(productUrl, variant?.id);
            this.updateVariantInputs(variant?.id);

            if (!variant) {
              this.setUnavailable();
              return;
            }

            this.updateMedia(variant?.featured_media?.id);

            const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
              const source = html.getElementById(`${id}-${this.sectionId}`);
              const destination = this.querySelector(`#${id}-${this.dataset.section}`);
              if (source && destination) {
                destination.innerHTML = source.innerHTML;
                destination.classList.toggle('hidden', shouldHide(source));
              }
            };

            updateSourceFromDestination('price');
            updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));

            this.productForm?.toggleSubmitButton(
              html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
              window.variantStrings.soldOut
            );

            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            });
          };
        }

        updateVariantInputs(variantId) {
          this.querySelectorAll(
            `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
          ).forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }

        updateURL(url, variantId) {
          if (this.dataset.updateUrl === 'false') return;
          window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
        }

        setUnavailable() {
          this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

          const selectors = ['price', 'Sku']
            .map((id) => `#${id}-${this.dataset.section}`)
            .join(', ');
          document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
        }

        updateMedia(variantFeaturedMediaId) {
          if (!variantFeaturedMediaId) return;
          this.scrollsnap?.scrollToElementById(variantFeaturedMediaId);
        }

        get productForm() {
          return this.querySelector(`product-form`);
        }

        get variantSelectors() {
          return this.querySelector('variant-selects');
        }

        get sectionId() {
          return this.dataset.originalSection || this.dataset.section;
        }
      }
    );
  }


  class DynamicReload extends HTMLElement {
    postProcessHtmlCallbacks = [];
    constructor() {
      super();
    }

    connectedCallback() {
      this.initializeProductSwapUtility();
      this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      this.handleClick();
    }
    
    initializeProductSwapUtility() {
      this.postProcessHtmlCallbacks.push((newNode) => {
        window?.Shopify?.PaymentButton?.init();
        window?.ProductModel?.loadShopifyXR();
      });
    }

    handleClick() {
      const linksArray = [...this.querySelectorAll('a')];
      linksArray.forEach((link) => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleconstructURL(link);
        })
      })
    }

    updateURL(url) {
      window.history.replaceState({}, '', `${url}`);
    }
    
    handleconstructURL(link) {
      const handle = link.dataset.handle;
      const url = `/products/${handle}`
      const section_id = this.dataset.section;
      const productURL = `${url}?section_id=${section_id}`;
      this.loadProductSection(productURL, url);
    }
    
    async loadProductSection(productURL, url) {
      try {
        const response = await fetch(productURL);
        const html = await response.text();

        // Parse the returned HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const section = doc.querySelector('.main-product');

        // Inject the section into the container
        if (section) {
          document.querySelector('.main-product').innerHTML = section.innerHTML;
          this.updateURL(url);

          // reinit yotpo
          yotpoWidgetsContainer.initWidgets();

          // reinit desktop zoom
          enableZoomOnHover(2.5);

          //reinit mobile zoom 
          window.initPhotoSwipe();
          
          // reinit wishlist 
          function swymCallbackFn(swat){
            swat.initializeActionButtons(".main-product");
          }
          if(!window.SwymCallbacks){
            window.SwymCallbacks = [];
          }
          window.SwymCallbacks.push(swymCallbackFn);
        } else {
          console.error('Section not found in the fetched HTML.');
        }
      } catch (error) {
        console.error('Error loading product section:', error);
      }
    }
  }

  customElements.define('dynamic-reload', DynamicReload);